/**
 * Client color chips: persistent per-client colors in localStorage (hex), scoped by company.
 * Synced when clients are loaded in MainDrawer; add on new client, remove when client is removed from API list.
 * User can change color in ClientDetailsDialog with a full color picker.
 */

const STORAGE_KEY = 'clientColors';

/** Default chip color when none is set */
const DEFAULT_HEX = '#6b7280';

/** Pool of hex colors for random assignment (no duplicates) */
const COLOR_POOL = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
  '#6366f1', '#14b8a6', '#eab308', '#f97316', '#a855f7', '#d946ef', '#0ea5e9', '#22c55e',
];

export const getClientColorsStorageKey = (companyId) => {
  if (companyId == null || companyId === '') return null;
  return `${STORAGE_KEY}:${companyId}`;
};

/** Active company from tenant picker persistence (same key as tenantSlice). */
export const resolveActiveCompanyId = (companyId) => {
  if (companyId != null && companyId !== '') return companyId;
  try {
    const raw = localStorage.getItem('selectedCompany');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.selectedCompanyId || null;
  } catch {
    return null;
  }
};

/** One-time: move legacy unscoped key into the first company bucket that loads it. */
const migrateLegacyClientColorsIfNeeded = (companyId) => {
  const key = getClientColorsStorageKey(companyId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(key)) return;
    const legacy = localStorage.getItem(STORAGE_KEY);
    if (!legacy) return;
    localStorage.setItem(key, legacy);
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
};

/**
 * @param {string | number | null | undefined} companyId
 * @returns {{ [clientId: string]: string }} Map of client id -> hex color
 */
export function getStoredClientColors(companyId) {
  const resolved = resolveActiveCompanyId(companyId);
  if (!resolved) return {};
  try {
    migrateLegacyClientColorsIfNeeded(resolved);
    const raw = localStorage.getItem(getClientColorsStorageKey(resolved));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result = {};
    for (const [id, hex] of Object.entries(parsed)) {
      if (typeof hex === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hex)) {
        result[String(id)] = hex;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function saveClientColors(companyId, map) {
  const key = getClientColorsStorageKey(resolveActiveCompanyId(companyId));
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // ignore
  }
}

/**
 * Generate a random hex color (full 24-bit).
 * @returns {string} hex e.g. '#3b82f6'
 */
function randomHexColor() {
  const n = Math.floor(Math.random() * 0xffffff);
  return '#' + n.toString(16).padStart(6, '0');
}

/**
 * Pick a random color not in usedSet: prefer pool, then generate random hex if pool exhausted.
 * @param {Set<string>} usedSet
 * @returns {string} hex
 */
function pickRandomUnusedColor(usedSet) {
  const available = COLOR_POOL.filter((c) => !usedSet.has(c));
  if (available.length > 0) {
    return available[Math.floor(Math.random() * available.length)];
  }
  // More than 16 clients: generate random hex and avoid duplicates
  for (let attempt = 0; attempt < 50; attempt++) {
    const hex = randomHexColor();
    if (!usedSet.has(hex)) return hex;
  }
  return randomHexColor();
}

/**
 * Sync stored colors with the current client list from the API.
 * - If key does not exist: create it and assign each client a random color (no duplicates).
 * - If key exists: add new clients with random unused colors, remove entries for clients not in the list.
 * Call this when clients are loaded (e.g. MainDrawer after GET clients).
 * @param {string | number} companyId
 * @param {Array<{ id: string | number }>} clients - List of client objects with id
 */
export function syncClientColors(companyId, clients) {
  const resolved = resolveActiveCompanyId(companyId);
  if (!resolved) return;

  const ids = (clients || []).map((c) => (c.id != null ? String(c.id) : null)).filter(Boolean);
  const stored = getStoredClientColors(resolved);

  const next = {};
  const usedColors = new Set();

  // Keep existing assignments for clients still in the list; track used colors
  for (const id of ids) {
    const existing = stored[id];
    if (existing && /^#[0-9A-Fa-f]{6}$/.test(existing)) {
      next[id] = existing;
      usedColors.add(existing);
    }
  }

  // Assign random unused color to any client that doesn't have one
  for (const id of ids) {
    if (next[id]) continue;
    const color = pickRandomUnusedColor(usedColors);
    next[id] = color;
    usedColors.add(color);
  }

  saveClientColors(resolved, next);
}

/**
 * Add a single client color (e.g. after adding a new client). Picks a random unused color.
 * @param {string | number} companyId
 * @param {string | number} clientId
 */
export function addClientColor(companyId, clientId) {
  const resolved = resolveActiveCompanyId(companyId);
  if (!resolved || clientId == null) return;
  const map = getStoredClientColors(resolved);
  const used = new Set(Object.values(map));
  map[String(clientId)] = pickRandomUnusedColor(used);
  saveClientColors(resolved, map);
}

/**
 * Remove a client's color entry (e.g. when client is deleted). Sync also removes missing clients.
 * @param {string | number} companyId
 * @param {string | number} clientId
 */
export function removeClientColor(companyId, clientId) {
  const resolved = resolveActiveCompanyId(companyId);
  if (!resolved || clientId == null) return;
  const map = getStoredClientColors(resolved);
  delete map[String(clientId)];
  saveClientColors(resolved, map);
}

/**
 * Set color for one client (full color picker in ClientDetailsDialog).
 * @param {string | number} companyId
 * @param {string | number} clientId
 * @param {string} hexColor - e.g. '#3b82f6'
 */
export function setClientColor(companyId, clientId, hexColor) {
  const resolved = resolveActiveCompanyId(companyId);
  if (!resolved || clientId == null) return;
  const hex = typeof hexColor === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hexColor) ? hexColor : DEFAULT_HEX;
  const map = getStoredClientColors(resolved);
  map[String(clientId)] = hex;
  saveClientColors(resolved, map);
}

/**
 * Get stored hex color for a client.
 * @param {string | number} clientId
 * @param {string | number | null | undefined} [companyId] - defaults to active company from tenant storage
 * @returns {string} hex
 */
export function getClientColor(clientId, companyId) {
  const map = getStoredClientColors(companyId);
  const hex = map[String(clientId)];
  return hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : DEFAULT_HEX;
}

/**
 * Compute luminance (0–1) for contrast.
 * @param {string} hex
 * @returns {number}
 */
function getLuminance(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const [rs, gs, bs] = [r, g, b].map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Parse hex to 0–1 rgb. Returns null if invalid.
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number } | null}
 */
function hexToRgb(hex) {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  return {
    r: ((n >> 16) & 0xff) / 255,
    g: ((n >> 8) & 0xff) / 255,
    b: (n & 0xff) / 255,
  };
}

function rgbToHex(r, g, b) {
  const rr = Math.round(Math.max(0, Math.min(1, r)) * 255);
  const gg = Math.round(Math.max(0, Math.min(1, g)) * 255);
  const bb = Math.round(Math.max(0, Math.min(1, b)) * 255);
  return '#' + [rr, gg, bb].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * Mix hex with white. whiteRatio 0.85 = 85% white, 15% hex (light fill like status chips).
 * @param {string} hex
 * @param {number} whiteRatio
 * @returns {string}
 */
function lightenWithWhite(hex, whiteRatio) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const t = 1 - whiteRatio;
  return rgbToHex(
    rgb.r * t + whiteRatio,
    rgb.g * t + whiteRatio,
    rgb.b * t + whiteRatio
  );
}

/**
 * Darken hex by multiplying rgb (dark shade of same hue for text).
 * @param {string} hex
 * @param {number} factor
 * @returns {string}
 */
function darken(hex, factor) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb.r * factor, rgb.g * factor, rgb.b * factor);
}

/**
 * Get contrasting text color (black or white) for a hex background.
 * @param {string} hex
 * @returns {string}
 */
export function getContrastText(hex) {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return '#1f2937';
  return getLuminance(hex) > 0.4 ? '#1f2937' : '#f9fafb';
}

/**
 * Get chip styles from a hex (light fill, darker border, dark text). Used for preview and for client chips.
 * @param {string} hex
 * @returns {{ bgColor: string, textColor: string, borderColor: string }}
 */
export function getClientChipStylesFromHex(hex) {
  const h = hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : DEFAULT_HEX;
  return {
    bgColor: lightenWithWhite(h, 0.85),
    textColor: darken(h, 0.35),
    borderColor: h,
  };
}

/**
 * Get chip styles for a client: same look as status/priority chips — light fill, darker border, dark text.
 * @param {string | number} clientId
 * @param {string | number | null | undefined} [companyId] - defaults to active company from tenant storage
 * @returns {{ bgColor: string, textColor: string, borderColor: string }}
 */
export function getClientChipStyles(clientId, companyId) {
  const hex = getClientColor(clientId, companyId);
  return getClientChipStylesFromHex(hex);
}
